import { LocationOn } from "@mui/icons-material";
import { Box, Grid, Typography, debounce } from "@mui/material";
import parse from "autosuggest-highlight/parse";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormAutoCompleteElement } from "@rhf-kit/mui";
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAP_IMBED_API_KEY;

function loadScript(src: string, position: HTMLElement | null, id: string) {
    if (!position) {
        return;
    }

    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", id);
    script.src = src;
    position.appendChild(script);
}

/**
 * Places API (New) via the Maps JS `places` library. The legacy
 * AutocompleteService / PlacesService cannot be enabled on new Google Cloud
 * projects, so this is the only Places client in the dashboard.
 */
const placesLibrary = () =>
    google.maps.importLibrary("places") as Promise<google.maps.PlacesLibrary>;

// Same result types the legacy component kept (drops cities, regions).
const ADDRESS_TYPES = [
    "street_address",
    "premise",
    "subpremise",
    "street_number",
    "establishment",
];

interface MatchRange {
    offset: number;
    length: number;
}

interface PlaceType {
    description: string;
    place_id: string;
    main_text: string;
    secondary_text: string;
    main_text_matches: MatchRange[];
}

/**
 * Structured components -> form fields. Replaces the old comma split of
 * `formatted_address`, which broke on premises and zip-less results.
 */
function parseAddressComponents(
    components: readonly google.maps.places.AddressComponent[]
) {
    const get = (type: string, short = false) => {
        const c = components.find((x) => x.types.includes(type));
        return (short ? c?.shortText : c?.longText) ?? "";
    };
    const street = [get("street_number"), get("route", true)]
        .filter(Boolean)
        .join(" ");
    const unit = get("subpremise");
    return {
        street1:
            (street && unit ? `${street} #${unit}` : street) || get("premise"),
        city:
            get("locality") || get("sublocality_level_1") || get("postal_town"),
        state: get("administrative_area_level_1", true),
        zip: get("postal_code"),
    };
}

async function fetchSuggestions(input: string): Promise<PlaceType[]> {
    const { AutocompleteSuggestion } = await placesLibrary();
    const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: ["us"],
            language: "en-US",
        });
    return suggestions.flatMap(({ placePrediction: p }) => {
        if (!p || !p.types.some((t) => ADDRESS_TYPES.includes(t))) return [];
        return [
            {
                description: p.text.text,
                place_id: p.placeId,
                main_text: p.mainText?.text ?? p.text.text,
                secondary_text: p.secondaryText?.text ?? "",
                main_text_matches: (p.mainText?.matches ?? []).map((m) => ({
                    offset: m.startOffset,
                    length: m.endOffset - m.startOffset,
                })),
            },
        ];
    });
}

async function fetchAddress(placeId: string) {
    const { Place } = await placesLibrary();
    const place = new Place({ id: placeId });
    await place.fetchFields({ fields: ["addressComponents"] });
    if (!place.addressComponents) {
        throw new Error("Places details failed: no address components");
    }
    return parseAddressComponents(place.addressComponents);
}

interface IProps {
    label?: string;
    name?: string;
    zipCodeName?: string;
    stateName?: string;
    cityName?: string;
    required?: boolean;
}

export const AddressAutoComplete = ({
    label,
    name,
    zipCodeName,
    stateName,
    cityName,
    required,
}: IProps) => {
    const [loading, setLoading] = useState(false);
    const { setValue, watch } = useFormContext();

    const initialPlace: PlaceType = {
        description:
            watch("address1") !== "" ? (watch("address1") as string) : "",
        place_id: "",
        main_text: "",
        secondary_text: "",
        main_text_matches: [],
    };
    const [locationValue, setLocationValue] = useState<PlaceType | null>(
        initialPlace
    );
    const [addressInputValue, setAddressInputValue] = useState(
        watch("address1") !== "" ? watch("address1") : ""
    );

    const [autoCompleteOptions, setAutoCompleteOptions] = useState<PlaceType[]>(
        []
    );
    const loaded = useRef(false);

    if (typeof window !== "undefined" && !loaded.current) {
        if (!document.querySelector("#google-maps")) {
            loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&libraries=places`,
                document.querySelector("head"),
                "google-maps"
            );
        }

        loaded.current = true;
    }

    const fetch = useMemo(
        () =>
            debounce(
                (
                    input: string,
                    callback: (results: PlaceType[] | null) => void
                ) => {
                    setLoading(true);
                    fetchSuggestions(input).then(callback, (err: unknown) => {
                        console.warn("Address suggestions failed", err);
                        callback(null);
                    });
                },
                400
            ),
        []
    );

    useEffect(() => {
        let active = true;

        // The Maps script loads async; until it is on the page there is nothing
        // to ask. The next keystroke retries.
        if (!window.google?.maps) {
            return undefined;
        }

        if (addressInputValue === "" || !addressInputValue) {
            setAutoCompleteOptions([]);
            return;
        }

        fetch(addressInputValue as string, (results) => {
            setLoading(false);
            if (active) {
                setAutoCompleteOptions(results ?? []);
            }
        });

        return () => {
            active = false;
        };
    }, [addressInputValue, fetch]);

    return (
        <FormAutoCompleteElement
            id="address-auto-complete"
            label={label ?? "Address 1"}
            loading={loading}
            required={required}
            name={name ?? "address1"}
            isOptionEqualToValue={(option, value) =>
                option.place_id === value.place_id
            }
            getOptionLabel={(option) =>
                typeof option === "string"
                    ? option
                    : option.main_text || option.description
            }
            options={autoCompleteOptions}
            filterOptions={(x) => x}
            value={locationValue}
            autoComplete
            includeInputInList
            filterSelectedOptions
            noOptionsText="No locations"
            textFieldProps={{
                margin: "normal",
            }}
            onChange={(_, value: PlaceType, reason) => {
                if (reason === "clear") {
                    setLocationValue(null);
                    setAddressInputValue("");
                    return;
                }

                setLocationValue(value);
                fetchAddress(value.place_id).then(
                    (address) => {
                        setValue(name ?? "address1", address.street1);
                        setValue(cityName ?? "city", address.city);
                        setValue(stateName ?? "state", address.state);
                        setValue(zipCodeName ?? "zipCode", address.zip);
                    },
                    (err: unknown) => {
                        console.warn("Address details failed", err);
                    }
                );
            }}
            onInputChange={(_event, newInputValue) => {
                setLocationValue({
                    ...initialPlace,
                    description: newInputValue,
                });
                setValue("address1", newInputValue);
                setAddressInputValue(newInputValue);
            }}
            fullWidth
            renderOption={(props, option) => {
                const parts = parse(
                    option.main_text,
                    option.main_text_matches.map((match: MatchRange) => [
                        match.offset,
                        match.offset + match.length,
                    ])
                );

                return (
                    <li
                        {...props}
                        key={`${option.description}-${option.place_id}`}
                    >
                        <Grid container alignItems="center">
                            <Grid item sx={{ display: "flex", width: 44 }}>
                                <LocationOn sx={{ color: "text.secondary" }} />
                            </Grid>
                            <Grid
                                item
                                sx={{
                                    width: "calc(100% - 44px)",
                                    wordWrap: "break-word",
                                }}
                            >
                                {parts.map((part, index) => (
                                    <Box
                                        key={index}
                                        component="span"
                                        sx={{
                                            fontWeight: part.highlight
                                                ? "bold"
                                                : "regular",
                                        }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {option.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};
